import { useContext, useEffect, useMemo, useState } from 'react';
import { graphqlFetch } from '@shared/api';
import * as z from 'zod';
import { QueryClient, QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	useNavigate,
	UseNavigateResult,
	useParams,
	useSearch,
} from '@tanstack/react-router';
import { issueQueryDocument } from '@shared/api/documents/issue';

import {
	Article,
	clone,
	Issue,
	isNumeric,
	USER_ROLE,
	ISSUE_LANG_KEY,
	Style,
	LangEnum,
} from '@/constants';
import config from '@/config';

import { useIssueNotFound, useServiceWorker } from '@shared/lib/hooks';
import { AppContext } from '@/App.context';
import { getQueryCacheOptions } from '@shared/query-client/QueryClient';
import {
	AUTH_KEY,
	isAccessTokenExpired,
	isRefreshTokenExpired,
	logoutUserJwt,
	REFRESH_TOKEN_KEY,
} from '@shared/auth/Authentication.hooks';
import { refreshAccessToken } from '@features/login/Auth.hooks';
import { buildStyles } from '@/components/article/StyleBuilder';
import { Styles } from '@/components/article/Styles';

export const ArticlesSortKeys = z
	.enum(['RELEASE_DATE', 'CREATED', 'UPDATED', 'INDEX'])
	.optional();

export const issueSearchSchema = z.object({
	lang: LangEnum,
	limit: z.number().optional().catch(undefined),
	sortKey: ArticlesSortKeys.catch(undefined),
	reverse: z.boolean().optional(),
});

export type IssueSearch = z.infer<typeof issueSearchSchema>;

/**
 * React hook that load data from navigation
 *
 */
export function useLoad(): NavigationResponse {
	const { id, articleId } = useParams({ strict: false });
	const issueId = id;
	const [currentArticle, setCurrentArticle] = useState<Article | null>(null);
	const { issue, isLoading, isUnavailableOffline, error } = useIssue(
		`${issueId}`,
	);

	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!issue.articles) {
			return;
		}
		let art = issue.articles[0];
		for (const article of issue.articles) {
			if (article.id === articleId || article.slug === articleId) {
				art = article;
				break;
			}
		}
		setCurrentArticle(art);
	}, [issue, articleId]);

	return {
		issue,
		currentArticle,
		setCurrentArticle,
		isLoading,
		isUnavailableOffline,
		error,
	};
}

/**
 * React hook that returns the details of the issue
 *
 * @param id - Props that gets the issue by it's id
 *
 */
export function useIssue(id: string): GetIssueResponse {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const search = useSearch({ strict: false });

	const queryKey = [
		'issue',
		id,
		{
			limit: search?.limit,
			sortKey: search?.sortKey,
			reverse: search?.reverse,
		},
	];
	const { onlineStatus: isOnline } = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null | undefined>();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: config.app?.cache?.ttl?.getIssue,
	});

	// Before even trying to load: if we're offline and there's no cached
	// data for this exact query key, we already have the answer -- no need
	// to run the query (and briefly show a loading state) just to have it
	// immediately resolve to "nothing to show". getQueryData is a
	// synchronous, in-memory read, safe to call during render.
	const isUnavailableOffline =
		!isOnline && queryClient.getQueryData(queryKey) === undefined;

	const { isPending, error, data } = useQuery<IssueQuery | null>({
		queryKey,
		networkMode: 'always',
		enabled: !isUnavailableOffline,
		queryFn: async () => {
			return await getIssue({
				id,
				queryClient,
				queryKey,
				isOnline,
				navigate,
				limit: search?.limit,
				sortKey: search?.sortKey,
				reverse: search?.reverse,
			});
		},
		staleTime: cacheOptions.staleTime,
		gcTime: cacheOptions.gcTime,
		retry: isOnline ? 3 : 0,
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;

		setIssue(clone(data.issue));
	}, [data]);

	if (issue && issue.styles) {
		issue.styles = buildStyles(issue.styles);
	}

	return {
		isLoading: isUnavailableOffline ? false : isPending,
		isUnavailableOffline,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

async function getIssue({
	id,
	queryClient,
	queryKey,
	isOnline,
	navigate,
	limit,
	sortKey,
	reverse,
}: {
	id: string | number;
	queryClient: QueryClient;
	queryKey: QueryKey;
	isOnline: boolean;
	navigate: UseNavigateResult<string>;
	limit?: number;
	reverse?: boolean;
	sortKey?: IssueSearch['sortKey'];
}): Promise<IssueQuery> {
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cached: IssueQuery | undefined = queryClient.getQueryData(queryKey);
	// The app is offline
	if (!isOnline) {
		// Defensive fallback only: useIssue()'s `enabled: !isUnavailableOffline`
		// normally stops this queryFn from running at all when offline with
		// nothing cached, so the view can show "unavailable offline"
		// immediately instead of waiting on a query that could only ever
		// resolve to nothing. This still covers the same case correctly if
		// it somehow runs anyway (e.g. isOnline flips mid-flight).
		if (!cached) {
			return { issue: null };
		}

		// If i have the data return it
		return cached;
	}

	const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';
	const variables = {
		id,
		isSlug: !isNumeric(id),
		limit,
		sortKey,
		reverse,
	};

	// Mirrors the old `let client` that started anonymous (getHTTPHeaders(false))
	// and was only rebuilt with credentials once the JWT/Cognito block below had
	// confirmed a usable access token. The final request at the bottom inherited
	// whichever of the two was current, so that decision has to be tracked
	// rather than hardcoded.
	let withAuth = false;

	// The user is not authenticated so i won't request access token
	if (!isAuthenticated) {
		try {
			return graphqlFetch(issueQueryDocument, variables, {
				withAuth: false,
			}) as unknown as Promise<IssueQuery>;
		} catch (e) {
			console.error(e);
			if (cached) return cached;
		}
	}

	if (isAdmin) {
		return graphqlFetch(issueQueryDocument, variables) as unknown as Promise<IssueQuery>;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		// I always need the refresh token, if it's missing logout
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		if (!refreshToken) {
			logoutUserJwt();
			return { issue: null };
		}

		// The user is authenticated and the user is online
		const refreshExpired = isRefreshTokenExpired();
		if (refreshExpired) {
			logoutUserJwt();
			return { issue: null };
		}

		let accessExpired = isAccessTokenExpired();
		if (accessExpired) {
			for (let i = 0; i < 3; i++) {
				try {
					await refreshAccessToken(refreshToken);
					accessExpired = false;
					break;
				} catch (e) {
					console.error(e);
					continue;
				}
			}
		}

		if (accessExpired) {
			if (cached) return cached;
			navigate({
				to: '/',
			});
			return { issue: null };
		}

		withAuth = true;
	}

	return graphqlFetch(issueQueryDocument, variables, {
		withAuth,
	}) as unknown as Promise<IssueQuery>;
}

// Cast at all three graphqlFetch(issueQueryDocument, ...) call sites above:
// the generated type's nullability is honest where the shared Issue type
// claims required fields -- topic 5's "derive Issue/Article/Style from
// generated types" step owns reconciling that, not a per-call-site patch.
export interface IssueQuery {
	issue: Issue | null;
}

interface GetIssueResponse {
	isLoading: boolean;
	isUnavailableOffline: boolean;
	issue?: Issue | null;
	error?: Error | null;
}

interface NavigationResponse extends GetIssueResponse {
	currentArticle: Article | null | undefined;
	setCurrentArticle: (article: Article) => void;
}

export function useStylesMap(issue?: Issue | null): Map<string, Style> | null {
	return useMemo(() => {
		if (!issue) return null;
		const { styles } = issue;
		if (!styles) return new Map();

		return new Styles(issue.styles).styles;
	}, [issue]);
}

/**
 * React hook that load data from navigation
 *
 */
export function useTitle(args: TitleArgs) {
	const { article, lang } = args;

	useEffect(() => {
		if (!article) {
			return;
		}
		if (!lang) {
			return;
		}

		const { name, description } = article;

		if (name?.[lang]) {
			let title = fixToUtf8(name[lang])
			document.title = title;
		}

		if (description) {
			const metaHeader = document.querySelector(
				'meta[name="description"]',
			);
			if (metaHeader) {
				metaHeader.setAttribute('content', description);
			}
		}
	}, [article, lang]);
}

/**
 * Safely converts and repairs any broken, scrambled, or entity-encoded
 * dynamic text into clean UTF-8 Arabic text.
 * @param {string} text - The raw dynamic text string.
 * @returns {string} - Clean, properly encoded string.
 */
function fixToUtf8(text: string) {
    if (!text || typeof text !== 'string') return '';

    // Step 1: Decode HTML numerical entities (e.g., &#1593;&#1606;)
    if (text.includes('&')) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        text = doc.body.textContent || doc.documentElement.textContent || text;
    }

    // Step 2: Fix Mojibake/Scrambled text (e.g., "ÚäæÇä" or "Ø¹Ù†ÙˆØ§Ù†")
    // This safely un-mangles bytes forced into Windows-1252 / ISO-8859-1 back to UTF-8
    try {
        const latinBytes = new Uint8Array(text.split('').map(char => char.charCodeAt(0)));
        const decodedText = new TextDecoder('utf-8', { fatal: true }).decode(latinBytes);
        
        // If the output doesn't contain fallback question mark blocks, return the healed string
        if (!decodedText.includes('')) {
            text = decodedText;
        }
    } catch {
        // If it throws an error, the text wasn't encoded as standard Mojibake.
        // We catch it and proceed with the text as-is.
    }

    return text;
}


interface TitleArgs {
	article: Article | null | undefined;
	lang: string;
}

/**
 * React hook that control font
 *
 */
export function useFont(): FontResponse {
	const [fontSize, setFontSize] = useState(
		localStorage.getItem('font-size')
			? parseFloat(`${localStorage.getItem('font-size')}`)
			: 62.5,
	);
	useEffect(() => {
		localStorage.setItem('font-size', `${fontSize}`);
	}, [fontSize]);
	return {
		fontSize,
		setFontSize,
	};
}

interface FontResponse {
	fontSize: number;
	setFontSize: (size: number) => void;
}

/**
 * React hook that runs when an article change
 *
 */
export function useArticle({ issue, article }: ArticleArgs) {
	const navigate = useNavigate();
	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!article) {
			return;
		}

		let hash: undefined | string;

		if (article.hash) {
			hash = article.hash;
		} else if (window.location.hash) {
			hash = window.location.hash;
		}

		navigate({
			replace: true,
			to: '/issues/$id/$articleId',
			params: {
				id: issue.slug || issue.id,
				articleId: article.slug || article.id,
			},
			search: (prevSearch: IssueSearch) => ({ ...prevSearch }),
			hash,
		});

		scrollTop(article.id);
	}, [issue, article, navigate]);
	useAutoPlayVideos({ article });
}

interface ArticleArgs {
	issue?: Issue | null | undefined;
	article: Article | null;
}

function useAutoPlayVideos({ article }: ArticleArgs) {
	useEffect(() => {
		if (!article) {
			return;
		}
		const articleNode = document.getElementById(`article-${article.id}`);
		if (!articleNode) {
			return;
		}

		/*Check for video elements*/
		const videoNodes = articleNode.querySelectorAll('video');
		for (const node of videoNodes) {
			if (node.hasAttribute('autoplay')) {
				node.play();
			}
		}
	}, [article]);
}

/**
 * React hook that download images in the background
 *
 */
export function useDownloadAssets(args: DownloadImagesArgs) {
	const { isReady, sendMessage } = useServiceWorker();
	const { issue } = args;
	useEffect(() => {
		if (!isReady) return; // Service worker is not ready
		if (!issue) return; // Issue is not set
		if (!issue.articles) return; // Issue do not have any articles

		const images = [
			...issue.articles.reduce(reduceArticleImages, new Set<string>()),
		];

		sendMessage({
			type: 'DOWNLOAD_BACKGROUND_IMAGES',
			payload: {
				images,
				IssueID: issue.id,
			},
		});
	}, [issue, isReady, sendMessage]);
}

interface DownloadImagesArgs {
	issue: Issue | null;
}

/**
 * React hook that handles the article language
 *
 */

export function useArticleLang({
	article,
	isLoading,
	languages,
	setLanguage,
}: ArticleLangArgs): void {
	const { lang } = useContext(AppContext);
	const navigate = useNavigate();
	const search: IssueSearch = useSearch({ strict: false });
	useEffect(() => {
		if (!article || isLoading || !languages.size) return;
		const issueLang = getIssueLang(lang);
		if (search?.lang) {
			if (languages.has(search.lang)) {
				localStorage.setItem(ISSUE_LANG_KEY, search.lang);
				setLanguage(search.lang);
				return;
			}

			// If the lang is passed but is not available i remove it

			navigate({
				to: '.',
				search: (prevSearch: IssueSearch) => {
					delete prevSearch.lang;
					return prevSearch;
				},
			});
			setLanguage(issueLang);
			return;
		}
		setLanguage(issueLang);
	}, [article, search, isLoading, languages, navigate, setLanguage, lang]);
}

interface ArticleLangArgs {
	article: Article | null | undefined;
	isLoading: boolean;
	languages: Set<string>;
	setLanguage: React.Dispatch<React.SetStateAction<string>>;
}

function getIssueLang(lang: string): string {
	if (!localStorage.getItem(ISSUE_LANG_KEY)) {
		localStorage.setItem(ISSUE_LANG_KEY, lang);
	}

	return `${localStorage.getItem(ISSUE_LANG_KEY)}`;
}

// `article.components` is a JSON scalar with no schema shape (see
// constants.ts's Article doc comment) -- this is the minimal recursive
// shape this background-image walk actually reads, not the full CMS
// component union (see components/article/virtual/component.type.ts for
// that).
interface ComponentImageNode {
	components?: Array<ComponentImageNode>;
	columns?: Array<Array<ComponentImageNode>>;
	images?: Array<{ imageurl?: string }>;
	imageurl?: string;
}

function reduceArticleImages(acc: Set<string>, article: Article) {
	const components = article.components as
		| Array<ComponentImageNode>
		| null
		| undefined;
	if (!components) {
		article.components = null;
		return acc;
	}
	return components.reduce(reduceComponentImages, acc);
}

function reduceComponentImages(acc: Set<string>, component: ComponentImageNode) {
	const { components, columns, images, imageurl } = component;
	if (components) {
		acc = components.reduce(reduceComponentImages, acc);
	}
	// `columns`/`tabs` components nest their children one level deeper than
	// a plain `components` array: `columns` is an array of columns, each
	// itself an array of components (see Columns.tsx's `columns` prop /
	// Tabs.tsx's `columns[tabIndex]`) -- without this, every image nested
	// inside a column or tab was invisible to the background prefetcher.
	if (columns) {
		acc = columns.reduce(
			(acc: Set<string>, column: Array<ComponentImageNode>) =>
				column.reduce(reduceComponentImages, acc),
			acc,
		);
	}
	if (imageurl) {
		acc.add(imageurl);
	}
	// Gallery components carry their images as an array of
	// { imageurl, caption } objects rather than a single `imageurl`.
	if (images) {
		for (const image of images) {
			if (image?.imageurl) {
				acc.add(image.imageurl);
			}
		}
	}
	return acc;
}

/*function reduceVideos(acc: Set<string>, article: any) {
	return article.components.reduce(reduceVideo, acc);
}

function reduceVideo(acc: Set<string>, component: any) {
	const { components, moviepath } = component;
	if (components) {
		return components.reduce(reduceComponents, acc);
	}

	if (moviepath) {
		acc.add(moviepath);
	}
	return acc;
}*/



function scrollTop(articleId: string) {
	const el = document.getElementById(`article-${articleId}`);
	if (!el) {
		return;
	}

	const options: ScrollToOptions = {
		top: 0,
		left: 0,
		behavior: 'instant',
	};

	el.scroll(options);
}
